<template>
  <div class="login-page3">
    <span class="back-site">FA</span>

    <div class="hero">
      <div class="hero-inner">
        <div class="logo-wrap">
          <img :src="`${publicPath}img/sinaweb.png`" alt="loptop background" class="plane" />
        </div>

        <div class="hero-text">
          <h1>سیستم‌های راهبردی؛ <br />پاسخ‌گوی نیازهای امروز سازمان‌ها</h1>
          <BaseButton title="اطلاعات بیشتر" variant="outline" type="button" />
        </div>
      </div>
    </div>

    <div class="auth-card">
      <div class="card-inner">
        <h3 class="card-title">{{ forgotPasswordMode ? 'بازیابی رمز عبور' : 'ورود به سامانه سیناپردازش' }}</h3>
        <div class="card-title-divider" aria-hidden="true"></div>

        <!-- LOGIN / REGISTER (animated) -->
        <transition name="tab" mode="out-in">
          <div v-if="activeTab === 'login'" key="login">
            <!-- STEP 1: identifier input (email or phone) -->
            <div v-if="step === 1" class="otp-step">
              <transition name="forgot-flow" mode="out-in">
                <div v-if="!forgotPasswordMode" key="login-default">
                  <FloatingInput v-model="identifier" :disabled="loadingSend" label="شماره موبایل یا نام کاربری"
                    @keyup.enter="handleSend" class="input" ref="identifierInput" autocomplete="username" type="text" />

                  <!-- Password Field -->
                  <FloatingInput v-model="password" :disabled="loadingSend" @keyup.enter="handleSend"
                    class="input password-input" label="رمز عبور" ref="passwordInput"
                    autocomplete="current-password" type="password" />
                  <!-- Turnstile widget placeholder (invisible). Rendered programmatically to keep UX clean -->
                  <!-- CAPTCHA disabled: <div ref="turnstileEl" style="display:none" aria-hidden="true"></div> -->

                  <BaseButton :title="loadingSend ? 'در حال بررسی...' : 'ورود'" variant="primary" size="lg" type="button"
                    :style="{ width: '100%', marginTop: '18px', fontSize: '22px' }"
                    :loading="loadingSend" :disabled="sendingDisabled" @click="handleSend" />

                  <div class="center-btn">
                    <BaseButton title="فراموشی رمز عبور" variant="ghost" type="button"
                      @click="enterForgotPasswordMode" />
                  </div>
                  <p class="error" v-if="error">{{ error }}</p>
                  <div class="divider">یا</div>

                  <div class="center-btn">
                    <BaseButton :title="''" variant="outline" type="button" aria-label="ورود با گوگل"
                      :style="{ padding: '8px', minWidth: '40px', minHeight: '40px', lineHeight: '0' }"
                      :loading="googleLoading" @click="onGoogle">
                      <GoogleSvg class="g-icon" aria-hidden="true" />
                    </BaseButton>
                  </div>

                  <p class="terms">ورود به معنای پذیرش
                    <a href="https://www.sinapardazesh.ir/terms-of-service" target="_blank" rel="noopener noreferrer"
                      title="شرایط استفاده">شرایط استفاده</a>
                    و
                    <a href="https://www.sinapardazesh.ir/privacy-policy" target="_blank" rel="noopener noreferrer"
                      title="سیاست حفظ حریم خصوصی">سیاست حفظ حریم خصوصی</a>
                    است
                  </p>
                </div>

                <div v-else key="forgot-password">
                  <transition name="forgot-step" mode="out-in">
                    <div v-if="forgotPasswordStep === 1" key="forgot-identifier">
                      <FloatingInput v-model="forgotPasswordIdentifier" :disabled="loadingSend"
                        @keyup.enter="handleForgotPasswordSendCode" class="input"
                        label="شماره موبایل یا نام کاربری" ref="identifierInput" autocomplete="username"
                        type="text" />
                      <p class="links">جهت بازیابی رمز عبور لطفا شماره موبایل یا نام کاربری خود را وارد نمایید.</p>

                      <div class="forgot-actions">
                        <BaseButton title="ارسال کد بازیابی رمز" variant="primary" type="button"
                          block :loading="loadingSend" :disabled="!forgotPasswordIdentifier.trim()"
                          @click="handleForgotPasswordSendCode" />
                        <BaseButton title="بازگشت به صفحه ورود" variant="secondary" type="button"
                          block @click="backToLoginFromForgot" />
                      </div>
                    </div>

                    <div v-else-if="forgotPasswordStep === 2" key="forgot-code">
                      <FloatingInput :model-value="forgotPasswordCode" ref="codeInput" class="input"
                        inputmode="numeric" maxlength="6" autocomplete="one-time-code" :disabled="loadingVerify"
                        @update:model-value="handleForgotPasswordCodeInput" @keyup.enter="handleForgotPasswordVerify"
                        label="کد دریافتی" type="text" />

                      <div class="forgot-code-meta">
                        <p class="forgot-code-help">لطفا کد دریافتی از پیامک را در کادر بالا وارد نمایید.</p>
                        <span class="forgot-code-timer" :class="{ 'timer-warning': forgotPasswordRemaining < 60 }">
                          {{ forgotPasswordTimerLabel }}
                        </span>
                      </div>

                      <BaseButton :title="loadingVerify ? 'در حال ثبت…' : 'ثبت'" variant="primary" type="button"
                        class="forgot-code-submit" block
                        :loading="loadingVerify" :disabled="forgotPasswordCode.length !== 6"
                        @click="handleForgotPasswordVerify" />

                      <BaseButton title="بازگشت به صفحه ورود" variant="secondary" type="button"
                        class="forgot-code-back" block :disabled="loadingVerify"
                        @click="backToLoginFromForgot" />
                    </div>

                    <div v-else key="forgot-reset-password" class="forgot-reset-step">
                      <p class="forgot-reset-text">کاربر گرامی، لطفا رمز عبور جدید خود را وارد نمایید.</p>

                      <FloatingInput v-model="forgotPasswordNewPassword" ref="forgotPasswordNewPasswordInput"
                        class="input" :disabled="forgotPasswordSubmitting"
                        @keyup.enter="handleForgotPasswordSetNewPassword" autocomplete="new-password"
                        label="رمز عبور جدید" type="password" />

                      <FloatingInput v-model="forgotPasswordConfirmNewPassword" class="input password-input"
                        :disabled="forgotPasswordSubmitting" @keyup.enter="handleForgotPasswordSetNewPassword"
                        autocomplete="new-password" label="تکرار رمز عبور جدید" type="password" />

                      <BaseButton :title="forgotPasswordSubmitting ? 'در حال ثبت...' : 'ثبت'" variant="primary"
                        type="button" class="forgot-reset-submit" block
                        :loading="forgotPasswordSubmitting" @click="handleForgotPasswordSetNewPassword" />

                      <BaseButton title="بازگشت به صفحه ورود" variant="secondary" type="button"
                        class="forgot-reset-back" block :disabled="forgotPasswordSubmitting"
                        @click="backToLoginFromForgot" />
                    </div>
                  </transition>

                  <p class="error" v-if="error">{{ error }}</p>
                </div>
              </transition>
            </div>

            <!-- STEP 2: OTP code input -->
            <div v-else class="otp-step">
              <label class="field-label">کد تایید</label>
              <input v-model="code" ref="codeInput" class="input" inputmode="numeric" pattern="\d{6}" maxlength="6"
                autocomplete="one-time-code" :disabled="loadingVerify || expired" @keyup.enter="handleVerify"
                @input="handleOtpInput" placeholder="کد 6 رقمی" type="text" />

              <div class="otp-meta">
                <div class="timer" :class="{ 'timer-warning': remaining < 60 }">{{ timerLabel }}</div>
                <BaseButton title="ارسال دوباره کد" variant="ghost" type="button"
                  :loading="loadingSend" :disabled="resendDisabled" @click="resendOtp" />
              </div>

              <div class="otp-actions">
                <BaseButton :title="loadingVerify ? 'در حال بررسی…' : 'تایید و ورود'" variant="primary" type="button"
                  :style="{ width: '100%' }" :loading="loadingVerify" :disabled="expired" @click="handleVerify" />
              </div>

              <!-- secondary gray button under the confirm button to change identifier -->
              <div class="otp-secondary">
                <BaseButton title="تغییر شماره/ایمیل" variant="secondary" type="button" :disabled="loadingVerify"
                  @click.prevent="backToStep1" />
              </div>

              <p class="error" v-if="error">{{ error }}</p>

              <div class="divider">یا</div>

              <div class="center-btn">
                <BaseButton :title="''" variant="outline" type="button" aria-label="ورود با گوگل"
                  :style="{ padding: '8px', minWidth: '40px', minHeight: '40px', lineHeight: '0' }"
                  :loading="googleLoading" @click="onGoogle">
                  <GoogleSvg class="g-icon" aria-hidden="true" />
                </BaseButton>
              </div>

              <p class="terms">ورود به معنای پذیرش
                <a href="https://www.sinapardazesh.ir/terms-of-service" target="_blank" rel="noopener noreferrer"
                  title="شرایط استفاده">شرایط استفاده</a>
                و
                <a href="https://www.sinapardazesh.ir/privacy-policy" target="_blank" rel="noopener noreferrer"
                  title="سیاست حفظ حریم خصوصی">سیاست حفظ حریم خصوصی</a>
                است
              </p>
            </div>
          </div>

          <!-- REGISTER TAB -->
          <div v-else key="register" class="register-step">
            <!-- STEP 1: شماره موبایل -->
            <div v-if="registerStep === 1">
              <input v-model="registerPhone" :disabled="registerLoading" @keyup.enter="handleRegister" class="input"
                placeholder="شماره موبایل" ref="registerInput" type="text" />
              <p class="links">جهت ثبت نام لطفا شماره موبایل خود را وارد نمایید.</p>
              <BaseButton :title="registerLoading ? 'در حال ارسال…' : 'ثبت نام'" variant="primary" size="lg"
                type="button" :style="{ width: '100%', marginTop: '18px', fontSize: '22px' }"
                :loading="registerLoading" :disabled="!registerPhone.trim()" @click="handleRegister" />
              <p class="error" v-if="registerError">{{ registerError }}</p>
            </div>

            <!-- STEP 2: کد تایید -->
            <div v-else class="otp-step">
              <label class="field-label">کد تایید</label>
              <input v-model="registerCode" ref="registerCodeInput" class="input" inputmode="numeric" pattern="\d{6}"
                maxlength="6" autocomplete="one-time-code" :disabled="registerVerifyLoading || registerExpired"
                @keyup.enter="handleRegisterVerify" @input="handleRegisterOtpInput" placeholder="کد 6 رقمی"
                type="text" />

              <div class="otp-meta">
                <div class="timer" :class="{ 'timer-warning': registerRemaining < 60 }">{{ registerTimerLabel }}</div>
                <BaseButton title="ارسال دوباره کد" variant="ghost" type="button"
                  :loading="registerLoading" :disabled="registerResendDisabled" @click="resendRegisterOtp" />
              </div>

              <div class="otp-actions">
                <BaseButton :title="registerVerifyLoading ? 'در حال بررسی…' : 'تایید و ثبت نام'" variant="primary"
                  type="button" :loading="registerVerifyLoading" :disabled="registerExpired" @click="handleRegisterVerify" />
              </div>

              <div class="otp-secondary">
                <BaseButton title="تغییر شماره" variant="secondary" type="button" :disabled="registerVerifyLoading"
                  @click.prevent="backToRegisterStep1" />
              </div>

              <p class="error" v-if="registerError">{{ registerError }}</p>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>

  <!-- reuse the global footer (same as Overview/SoftwareLayout) -->
  <FooterLicense />
  <InfoModal
    v-model="sessionExpiredModalOpen"
    title="پایان نشست"
    :show-learn-link="false"
    default-content="برای حفظ امنیت، نشست شما منقضی شد. لطفاً دوباره وارد شوید."
    id="session-expired"
  />
  <InfoModal
    v-model="systemInfoModalOpen"
    title="اطلاع رسانی"
    :show-learn-link="false"
    id="system-info"
    @close="resolveSystemInfoModal"
  >
    <p class="system-info-message" v-html="systemInfoMessage"></p>
  </InfoModal>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
import { useRoute } from 'vue-router'
import FooterLicense from '@/components/Common/FooterLicense.vue'
import InfoModal from '@/components/Common/InfoModal.vue'
import BaseButton from '@/components/UiCompunents/BaseButton.vue'
import apiClient from '@/axios'
import axios from 'axios'
import router from '@/router'
import { useESFSettingsStore } from '@/stores/ESFsettings'
import { useAuthenticateStore } from '@/stores/common/authenticate'
// import { useSettingStore } from '@/stores/clinic/setting.js'
import { useToast } from 'vue-toastification'
import { useTheme } from '@/composables/useTheme.js'
import FloatingInput from '@/components/UiCompunents/FloatingInput.vue'
import GoogleSvg from '@/components/svgs/GoogleSvg.vue'

// site key support for both vite and vue-cli env styles
const SITE_KEY = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_TURNSTILE_SITE_KEY)
  || process.env.VUE_APP_TURNSTILE_SITE_KEY

const publicPath = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.BASE_URL) || process.env.BASE_URL || '/'

// Toggle captcha (Cloudflare Turnstile) during development
// set to false to disable/skip captcha checks
const CAPTCHA_ENABLED = false

// store & utilities used for post-login setup
// const settingStore = useSettingStore()
// const { fetchSetting } = settingStore
const toast = useToast()
const route = useRoute()
const esfSettingsStore = useESFSettingsStore()
const authenticate = useAuthenticateStore()
const { setTheme } = useTheme()

// reactive state
const step = ref(1)
const identifier = ref('')
const password = ref('')
const code = ref('')
const loadingSend = ref(false)
const loadingVerify = ref(false)
const googleLoading = ref(false)
const error = ref('')
const sessionExpiredModalOpen = ref(false)
const systemInfoModalOpen = ref(false)
const systemInfoMessage = ref('')
const forgotPasswordMode = ref(false)
const forgotPasswordStep = ref(1)
const forgotPasswordIdentifier = ref('')
const forgotPasswordCode = ref('')
const forgotPasswordNewPassword = ref('')
const forgotPasswordConfirmNewPassword = ref('')
const forgotPasswordSubmitting = ref(false)
const forgotPasswordExpiryTimestamp = ref(null)
const forgotPasswordNow = ref(Date.now())
let forgotPasswordTimerInterval = null

// tabs & register state
const activeTab = ref('login')
const registerStep = ref(1)
const registerPhone = ref('')
const registerCode = ref('')
const registerLoading = ref(false)
const registerVerifyLoading = ref(false)
const registerError = ref('')
const registerSuccess = ref('')
const registerExpiryTimestamp = ref(null)
const registerNow = ref(Date.now())
const registerResendAvailableAt = ref(null)
let registerTimerInterval = null

const expiryTimestamp = ref(null)
const now = ref(Date.now())
const resendAvailableAt = ref(null)
let timerInterval = null

const turnstileWidgetId = ref(null)
const turnstileToken = ref('')
const turnstileTokenExpiry = ref(0)
const awaitingTurnstile = ref(false)
let turnstileTimeoutId = null
let googleWindow = null
let googleInterval = null
let _onGoogleMessage = null
let resolveSystemInfoPromise = null

// DOM refs
const identifierInput = ref(null)
const passwordInput = ref(null)
const codeInput = ref(null)
const forgotPasswordNewPasswordInput = ref(null)
const turnstileEl = ref(null)
const registerInput = ref(null)
const registerCodeInput = ref(null)

const identifierPlaceholder = 'شماره موبایل یا نام کاربری'

const remaining = computed(() => {
  if (!expiryTimestamp.value) return 0
  return Math.max(0, Math.floor((expiryTimestamp.value - now.value) / 1000))
})

const timerLabel = computed(() => {
  const s = remaining.value
  const mm = String(Math.floor(s / 60)).padStart(2, '0')
  const ss = String(s % 60).padStart(2, '0')
  if (expired.value) return 'کد منقضی شده است'
  return `${mm}:${ss} مانده تا پایان اعتبار کد`
})

const expired = computed(() => remaining.value <= 0)

const resendDisabled = computed(() => {
  if (!resendAvailableAt.value) return true
  if (expired.value) return false
  return Date.now() < resendAvailableAt.value
})

const sendingDisabled = computed(() => identifier.value.trim().length === 0 || password.value.trim().length === 0)

const forgotPasswordRemaining = computed(() => {
  if (!forgotPasswordExpiryTimestamp.value) return 0
  return Math.max(0, Math.floor((forgotPasswordExpiryTimestamp.value - forgotPasswordNow.value) / 1000))
})

const forgotPasswordTimerLabel = computed(() => {
  const s = forgotPasswordRemaining.value
  const mm = String(Math.floor(s / 60)).padStart(2, '0')
  const ss = String(s % 60).padStart(2, '0')
  return `${mm}:${ss}`
})

// Register computed properties
const registerRemaining = computed(() => {
  if (!registerExpiryTimestamp.value) return 0
  return Math.max(0, Math.floor((registerExpiryTimestamp.value - registerNow.value) / 1000))
})

const registerTimerLabel = computed(() => {
  const s = registerRemaining.value
  const mm = String(Math.floor(s / 60)).padStart(2, '0')
  const ss = String(s % 60).padStart(2, '0')
  if (registerExpired.value) return 'کد منقضی شده است'
  return `${mm}:${ss} مانده تا پایان اعتبار کد`
})

const registerExpired = computed(() => registerRemaining.value <= 0)

const registerResendDisabled = computed(() => {
  if (!registerResendAvailableAt.value) return true
  if (registerExpired.value) return false
  return Date.now() < registerResendAvailableAt.value
})

function validateIdentifier(id) {
  const value = (id || '').trim()
  if (!value) return { ok: false, message: 'لطفاً ایمیل یا شماره موبایل را وارد کنید.' }
  const emailRE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const phoneRE = /^\+?\d{9,15}$/
  if (emailRE.test(value)) return { ok: true, type: 'email' }
  if (phoneRE.test(value)) return { ok: true, type: 'phone' }
  return { ok: false, message: 'فرمت وارد شده معتبر نیست. ایمیل یا شماره موبایل را بررسی کنید.' }
}

function switchTab(name) {
  activeTab.value = name
  // reset errors and context when switching
  error.value = ''
  forgotPasswordMode.value = false
  forgotPasswordStep.value = 1
  forgotPasswordIdentifier.value = ''
  forgotPasswordCode.value = ''
  forgotPasswordNewPassword.value = ''
  forgotPasswordConfirmNewPassword.value = ''
  forgotPasswordSubmitting.value = false
  clearForgotPasswordTimer()
  registerError.value = ''
  registerSuccess.value = ''
  registerStep.value = 1
  registerCode.value = ''
  clearRegisterTimer()
  if (name === 'login') {
    nextTick(() => { if (identifierInput.value) identifierInput.value.focus() })
  } else {
    nextTick(() => { if (registerInput.value) registerInput.value.focus() })
  }
}

function enterForgotPasswordMode() {
  error.value = ''
  forgotPasswordMode.value = true
  forgotPasswordStep.value = 1
  forgotPasswordIdentifier.value = identifier.value.trim()
  forgotPasswordCode.value = ''
  forgotPasswordNewPassword.value = ''
  forgotPasswordConfirmNewPassword.value = ''
  forgotPasswordSubmitting.value = false
  clearForgotPasswordTimer()
  nextTick(() => { if (identifierInput.value) identifierInput.value.focus() })
}

function backToLoginFromForgot() {
  error.value = ''
  forgotPasswordMode.value = false
  forgotPasswordStep.value = 1
  forgotPasswordIdentifier.value = ''
  forgotPasswordCode.value = ''
  forgotPasswordNewPassword.value = ''
  forgotPasswordConfirmNewPassword.value = ''
  forgotPasswordSubmitting.value = false
  clearForgotPasswordTimer()
  nextTick(() => { if (identifierInput.value) identifierInput.value.focus() })
}

async function handleForgotPasswordSendCode() {
  error.value = ''
  if (!forgotPasswordIdentifier.value.trim()) {
    error.value = 'لطفاً شماره موبایل یا نام کاربری را وارد کنید.'
    return
  }
  loadingSend.value = true
  try {
    await authenticate.sendOtpForForgotPassword({
      mobileNumber: forgotPasswordIdentifier.value.trim()
    })
    forgotPasswordStep.value = 2
    forgotPasswordCode.value = ''
    startForgotPasswordTimer()
    nextTick(() => { if (codeInput.value) codeInput.value.focus() })
  } catch (e) {
    error.value = 'خطا در ارسال کد تایید'
  } finally {
    loadingSend.value = false
  }
}

function handleForgotPasswordCodeInput(value) {
  forgotPasswordCode.value = String(value || '').replace(/\D/g, '').slice(0, 6)
}

async function handleForgotPasswordVerify() {
  error.value = ''
  if (!/^\d{6}$/.test(forgotPasswordCode.value)) {
    error.value = 'کد باید 6 رقمی باشد'
    return
  }
  forgotPasswordStep.value = 3
  forgotPasswordNewPassword.value = ''
  forgotPasswordConfirmNewPassword.value = ''
  clearForgotPasswordTimer()
  nextTick(() => { if (forgotPasswordNewPasswordInput.value) forgotPasswordNewPasswordInput.value.focus() })
}

function startForgotPasswordTimer() {
  clearForgotPasswordTimer()
  forgotPasswordNow.value = Date.now()
  forgotPasswordExpiryTimestamp.value = forgotPasswordNow.value + 180000
  forgotPasswordTimerInterval = setInterval(() => {
    forgotPasswordNow.value = Date.now()
    if (forgotPasswordRemaining.value <= 0) clearForgotPasswordTimer()
  }, 250)
}

function clearForgotPasswordTimer() {
  if (forgotPasswordTimerInterval) {
    clearInterval(forgotPasswordTimerInterval)
    forgotPasswordTimerInterval = null
  }
  forgotPasswordExpiryTimestamp.value = null
}

async function handleForgotPasswordSetNewPassword() {
  error.value = ''
  const newPassword = forgotPasswordNewPassword.value.trim()
  const confirmNewPassword = forgotPasswordConfirmNewPassword.value.trim()

  if (!newPassword || !confirmNewPassword) {
    error.value = 'لطفا هر دو فیلد رمز عبور را وارد کنید.'
    return
  }

  if (newPassword.length < 6) {
    error.value = 'رمز عبور باید حداقل 6 کاراکتر باشد.'
    return
  }

  if (newPassword !== confirmNewPassword) {
    error.value = 'رمز عبور و تکرار آن یکسان نیستند.'
    return
  }

  forgotPasswordSubmitting.value = true
  try {
    await authenticate.ChangeForgotPassword({
      userName: forgotPasswordIdentifier.value.trim(),
      newPassword,
      otpCode: forgotPasswordCode.value
    })
    backToLoginFromForgot()
  } catch (e) {
    error.value = 'خطا در ثبت رمز عبور جدید'
  } finally {
    forgotPasswordSubmitting.value = false
  }
}

async function handleRegister() {
  registerError.value = ''

  // بررسی خالی بودن فیلد
  if (!registerPhone.value.trim()) {
    registerError.value = 'لطفاً شماره موبایل را وارد کنید.'
    return
  }
  try {
    const base = (API_URL || '').replace(/\/$/, '')
    const response = await axios.get(`${base}/Authenticate/SendOtpForRegisterCustomer`, {
      params: {
        phoneNumber: registerPhone.value.trim()
      },
      headers: { 'Content-Type': 'application/json' }
    })

    console.log(response.data)
    const data = response.data || {}
    const raw = response.data
    const status = (data.status || data.Status || '').toString().toLowerCase()
    const message = data.message || data.Message || ''

    // اگر پاسخ خطا باشد، به مرحله ورود کد نرو (در همان مرحله فعلی بمان)
    const isErrorString = status === 'error'
    const isSuccess = status === 'success' || status === 'ok'
    if (!isSuccess || isErrorString) {
      registerError.value = message || 'خطا در ارسال کد'
      return
    }

    // فقط در صورت موفقیت، به مرحله 2 برو و تایمر/استوریج را تنظیم کن
    registerError.value = ''
    registerExpiryTimestamp.value = Date.now() + 180000
    registerResendAvailableAt.value = Date.now() + 60000
    localStorage.setItem('registerOtpExpiry', String(registerExpiryTimestamp.value))
    localStorage.setItem('registerPhone', String(registerPhone.value.trim()))
    localStorage.setItem('registerResendAt', String(registerResendAvailableAt.value))

    registerStep.value = 2
    startRegisterTimer()

    await nextTick()
    if (registerCodeInput.value) registerCodeInput.value.focus()
    return
  } catch (err) {
    // axios throws for non-2xx status codes; inspect err.response.data when available
    if (err && err.response && err.response.data) {
      try {
        const d = err.response.data
        registerError.value = d.message || JSON.stringify(d)
      } catch (e) {
        registerError.value = String(err)
      }
    } else {
      registerError.value = (err && err.message) || String(err) || 'خطا در ارسال'
    }
    // در صورت خطا، به مرحله ورود کد نرو
    return
  }
}

function startRegisterTimer() {
  clearRegisterTimer()
  registerNow.value = Date.now()
  registerTimerInterval = setInterval(() => {
    registerNow.value = Date.now()
    if (registerRemaining.value <= 0) clearRegisterTimer()
  }, 250)
}

function clearRegisterTimer() {
  if (registerTimerInterval) { clearInterval(registerTimerInterval); registerTimerInterval = null }
  registerExpiryTimestamp.value = registerExpiryTimestamp.value && registerRemaining.value > 0 ? registerExpiryTimestamp.value : null
}

function getPayloadSources(payload = {}) {
  return [
    payload,
    payload?.result,
    payload?.data,
    payload?.result?.data,
    payload?.data?.result
  ].filter(value => value && typeof value === 'object')
}

function extractLoginData(payload = {}) {
  const sources = getPayloadSources(payload)

  const pick = (...keys) => {
    for (const source of sources) {
      for (const key of keys) {
        if (source[key] !== undefined && source[key] !== null) return source[key]
      }
    }
    return null
  }

  const companyInfo = pick('companyInfo', 'companyinfo')
  const storageInfo = pick('storageInfo', 'storageinfo')
  const modulePermission = pick('modulePermission', 'ModulePermission')
  const activeValue = pick('companyisactive', 'companyIsActive', 'isActive')
    ?? companyInfo?.companyisactive
    ?? companyInfo?.companyIsActive
    ?? companyInfo?.isActive
    ?? false

  return {
    token: pick('token', 'accessToken', 'jwtToken'),
    companyInfo,
    storageInfo,
    modulePermission,
    companyisactive: activeValue === true || activeValue === 'true',
    ismaster: pick('ismaster', 'isMaster') ?? false,
    message: pick('message', 'Message')
  }
}

function extractGlobalInformationMessage(payload = {}) {
  for (const source of getPayloadSources(payload)) {
    const globalInformation = source.globalInformation || source.GlobalInformation
    const messageInfo = Array.isArray(globalInformation)
      ? globalInformation[0]?.messageInfo || globalInformation[0]?.MessageInfo
      : null

    if (typeof messageInfo === 'string' && messageInfo.trim().length > 0) {
      return messageInfo
    }
  }

  return ''
}

function resolveSystemInfoModal() {
  if (resolveSystemInfoPromise) {
    resolveSystemInfoPromise()
    resolveSystemInfoPromise = null
  }
}

function showSystemInfoModal(message) {
  if (!message) return Promise.resolve()

  systemInfoMessage.value = message
  systemInfoModalOpen.value = true

  return new Promise((resolve) => {
    resolveSystemInfoPromise = resolve
  })
}

function persistLoginData(loginData) {
  localStorage.setItem('token', loginData.token)
  localStorage.setItem('ismaster', String(loginData.ismaster))
  localStorage.setItem('companyisactive', String(loginData.companyisactive))

  const calculateAccess = loginData.modulePermission?.activeCalculateAccess
    ?? loginData.modulePermission?.ActiveCalculateAccess
  if (calculateAccess !== undefined && calculateAccess !== null) {
    localStorage.setItem('ca', String(calculateAccess))
  } else {
    localStorage.removeItem('ca')
  }

  if (loginData.companyInfo) {
    localStorage.setItem('companyInfo', JSON.stringify(loginData.companyInfo))
  } else {
    localStorage.removeItem('companyInfo')
  }

  if (loginData.storageInfo) {
    localStorage.setItem('storageInfo', JSON.stringify(loginData.storageInfo))
  } else {
    localStorage.removeItem('storageInfo')
  }
}

const esfDefaultPageRoutes = {
  1: '/overview/ESF/dashboard',
  2: '/overview/ESF/CompanyGoodsAndService',
  3: '/overview/ESF/Buyers',
  4: '/overview/ESF/EStatement',
  5: '/overview/ESF/ESFsettings',
}

function getSettingsDefaultPage(settings = {}) {
  return settings?.defaultPage
    ?? settings?.DefaultPage
    ?? settings?.default_page
    ?? esfSettingsStore.settings?.defaultPage
    ?? esfSettingsStore.settings?.DefaultPage
    ?? esfSettingsStore.settings?.default_page
}

function getSettingsDefaultTheme(settings = {}) {
  return settings?.defaultTheme
    ?? settings?.DefaultTheme
    ?? settings?.default_theme
    ?? esfSettingsStore.settings?.defaultTheme
    ?? esfSettingsStore.settings?.DefaultTheme
    ?? esfSettingsStore.settings?.default_theme
}

function applyDefaultTheme(settings = {}) {
  const configuredTheme = getSettingsDefaultTheme(settings)
  const themeByValue = {
    1: 'light',
    2: 'dark',
    3: 'gray',
    4: 'cream'
  }
  const normalizedTheme = typeof configuredTheme === 'string'
    ? configuredTheme.trim().toLowerCase()
    : themeByValue[Number(configuredTheme)]
  const themeAliases = {
    روشن: 'light',
    سفید: 'light',
    تیره: 'dark',
    مشکی: 'dark',
    خاکستری: 'gray',
    طوسی: 'gray',
    کرم: 'cream'
  }
  const theme = themeAliases[normalizedTheme] || normalizedTheme
  if (['light', 'dark', 'gray', 'cream'].includes(theme)) setTheme(theme)
}

async function getDefaultLoginPath(settings = null) {
  try {
    const resolvedSettings = settings || await esfSettingsStore.fetchSettings()
    const defaultPage = Number(getSettingsDefaultPage(resolvedSettings))
    return esfDefaultPageRoutes[defaultPage] || '/overview/ESF/dashboard'
  } catch (error) {
    console.warn('fetch ESF default page failed', error)
    return '/overview/ESF/dashboard'
  }
}

async function redirectAfterLogin(isActive) {
  const settings = await esfSettingsStore.fetchSettings()
  applyDefaultTheme(settings)

  if (!isActive) {
    await router.push({ path: '/company-setup' })
    return
  }

  await router.push(await getDefaultLoginPath(settings))
}

async function handleRegisterVerify() {
  registerError.value = ''
  if (!/^\d{6}$/.test(registerCode.value)) { registerError.value = 'کد باید 6 رقمی باشد'; return }
  registerVerifyLoading.value = true
  try {
    // TODO: وصل به بک‌اند برای تایید کد ثبت نام
    const res = await verifyRegisterOtp(registerPhone.value.trim(), registerCode.value.trim())
    // شبیه‌سازی موقت
    await new Promise(resolve => setTimeout(resolve, 700))

    // موفقیت آمیز بود
    registerSuccess.value = 'ثبت نام با موفقیت انجام شد!'
    localStorage.removeItem('registerOtpExpiry')
    localStorage.removeItem('registerPhone')
    localStorage.removeItem('registerResendAt')
    clearRegisterTimer()

    // بعد از 2 ثانیه به تب لاگین برگرد
    setTimeout(() => {
      switchTab('login')
    }, 2000)
  } catch (err) {
    registerError.value = (err && err.message) || String(err) || 'خطا در تایید کد'
  } finally { registerVerifyLoading.value = false }
}

async function verifyRegisterOtp(registerPhone, registerCode) {
  // call backend VerifyOtp endpoint
  try {
    const resp = await axios.post(`${API_URL}/Authenticate/VerifyRegisterOtp`, {
      phoneNumber: registerPhone,
      otp: registerCode
    }, {
      headers: { 'Content-Type': 'application/json' }
    })

    const d = resp && resp.data ? resp.data : {}
    const loginData = extractLoginData(d)
    const token = loginData.token
    const systemMessage = extractGlobalInformationMessage(d)

    if (token) {
      persistLoginData(loginData)
      const isActive = loginData.companyisactive

      // fetch app settings and then redirect accordingly
      // try { await fetchSetting() } catch (e) { console.warn('fetchSetting failed', e) }

      await showSystemInfoModal(systemMessage)
      await redirectAfterLogin(isActive)

      // success
      if (toast && typeof toast.success === 'function') {
        toast.success('ورود با موفقیت انجام شد', { position: 'top-right', timeout: 1500 })
      }

      return { token }
    }

    throw new Error(d.message || d.Message || 'خطا در تایید کد')
  } catch (err) {
    // normalize axios errors
    if (err && err.response && (err.response.status === 401 || err.response.status === 400 || err.response.status === 500)) {
      const msg = (err.response.data && (err.response.data.message || JSON.stringify(err.response.data))) || 'خطا در اعتبارسنجی'
      throw new Error(msg)
    }
    throw err
  }
}

function resendRegisterOtp() {
  if (registerResendDisabled.value) return
  registerCode.value = ''
  handleRegister()
}

function backToRegisterStep1() {
  registerStep.value = 1
  registerCode.value = ''
  registerError.value = ''
  localStorage.removeItem('registerOtpExpiry')
  localStorage.removeItem('registerPhone')
  localStorage.removeItem('registerResendAt')
  clearRegisterTimer()
  nextTick(() => { if (registerInput.value) registerInput.value.focus() })
}

function handleRegisterOtpInput(e) {
  // فقط اعداد مجاز هستند
  e.target.value = e.target.value.replace(/\D/g, '')
  registerCode.value = e.target.value
}

function startTimer() {
  clearTimer()
  now.value = Date.now()
  timerInterval = setInterval(() => {
    now.value = Date.now()
    if (remaining.value <= 0) clearTimer()
  }, 250)
}

function clearTimer() {
  if (timerInterval) { clearInterval(timerInterval); timerInterval = null }
  expiryTimestamp.value = expiryTimestamp.value && remaining.value > 0 ? expiryTimestamp.value : null
}

function initTurnstile() {
  const tryRender = () => {
    if (!turnstileEl.value) return
    if (window.turnstile && typeof window.turnstile.render === 'function') {
      try {
        if (!SITE_KEY) { console.warn('TURNSTILE site key not set'); return }
        // NOTE: Turnstile no longer supports "invisible". Use a supported size and execute programmatically
        // to get the same UX (render small/flexible widget and call execute when needed).
        turnstileWidgetId.value = window.turnstile.render(turnstileEl.value, {
          sitekey: SITE_KEY,
          // use 'flexible' to adapt to available space; we call execute() when sending to mimic invisible flow
          size: 'flexible',
          action: 'otp_send',
          callback: (token) => {
            // received token from Turnstile
            try { if (turnstileTimeoutId) { clearTimeout(turnstileTimeoutId); turnstileTimeoutId = null } } catch (e) { console.warn(e) }
            turnstileToken.value = token
            turnstileTokenExpiry.value = Date.now() + 120000
            if (awaitingTurnstile.value) { awaitingTurnstile.value = false; doSend() }
          }
        })
      } catch (e) { console.warn('turnstile render failed', e) }
    } else { setTimeout(tryRender, 250) }
  }
  tryRender()
}



// ارسال OTP با کپچا (غیرفعال)
async function doSend() {
  error.value = ''
  loadingSend.value = true
  try {
    if (!turnstileToken.value || Date.now() > turnstileTokenExpiry.value) {
      error.value = 'لطفاً کپچا را کامل کنید.'
      try { if (window.turnstile && turnstileWidgetId.value !== null) window.turnstile.reset(turnstileWidgetId.value) } catch (e) { console.warn(e) }
      return
    }

    const payload = { identifier: identifier.value.trim(), turnstileToken: turnstileToken.value }
    const resp = await apiClient.post('/api/otp/send', payload)
    const data = resp && resp.data ? resp.data : {}
    // تایمر 3 دقیقه (180 ثانیه)
    expiryTimestamp.value = data.expiry || (Date.now() + 180000)
    resendAvailableAt.value = Date.now() + 60000
    localStorage.setItem('otpExpiry', String(expiryTimestamp.value))
    localStorage.setItem('otpIdentifier', String(identifier.value.trim()))
    localStorage.setItem('otpResendAt', String(resendAvailableAt.value))
    step.value = 2
    startTimer()
    await nextTick()
    if (codeInput.value) codeInput.value.focus()
    // فعال کردن WebOTP API برای خواندن خودکار کد
    requestWebOTP()
    try { if (window.turnstile && turnstileWidgetId.value !== null) window.turnstile.reset(turnstileWidgetId.value) } catch (e) { console.warn(e) }
    turnstileToken.value = ''
    turnstileTokenExpiry.value = 0
  } catch (err) {
    if (err && err.response && err.response.status === 403) {
      error.value = (err.response.data && err.response.data.message) || 'تأیید کپچا انجام نشد. دوباره تلاش کنید.'
    } else {
      error.value = (err && err.message) || String(err) || 'خطا در ارسال کد'
    }
    try { if (window.turnstile && turnstileWidgetId.value !== null) window.turnstile.reset(turnstileWidgetId.value) } catch (e) { console.warn(e) }
    turnstileToken.value = ''
    turnstileTokenExpiry.value = 0
  } finally { loadingSend.value = false }
}

error.value = ''

async function completeLogin(data) {
  const d = data || {}
  const loginData = extractLoginData(d)
  const token = loginData.token
  const systemMessage = extractGlobalInformationMessage(d)

  if (!token) {
    throw new Error(loginData.message || 'خطا در ورود')
  }

  persistLoginData(loginData)
  const isActive = loginData.companyisactive
  localStorage.removeItem('otpExpiry')
  localStorage.removeItem('otpIdentifier')
  localStorage.removeItem('otpResendAt')
  clearTimer()
  cancelWebOTP()

  await showSystemInfoModal(systemMessage)
  await redirectAfterLogin(isActive)

  if (toast && typeof toast.success === 'function') {
    toast.success('ورود با موفقیت انجام شد', { position: 'top-right', timeout: 1500 })
  }

  return { token }
}

async function handleSend() {

  error.value = ''

  // بررسی خالی بودن فیلد
  if (!identifier.value.trim()) {
    error.value = 'لطفاً ایمیل یا شماره موبایل را وارد کنید.'
    return
  }

  if (!password.value.trim()) {
    error.value = 'لطفاً رمز عبور را وارد کنید.'
    return
  }

  loadingSend.value = true
  try {
    const response = await axios.post(`${API_URL}/Authenticate/login`, {
      username: identifier.value.trim(),
      password: password.value.trim()
    }, {
      headers: { 'Content-Type': 'application/json' }
    })
    await completeLogin(response.data)
  } catch (err) {
    if (err?.response?.status === 401) {
      const message = 'نام کاربری یا کلمه عبور اشتباه است'
      error.value = message
      toast.error({ title: 'خطای ورود', message })
    } else if (err && err.response && err.response.data) {
      try {
        const d = err.response.data
        error.value = d.message || d.Message || JSON.stringify(d)
      } catch (e) {
        error.value = String(err)
      }
    } else {
      error.value = (err && err.message) || String(err) || 'خطا در ورود'
    }
  } finally {
    loadingSend.value = false
  }
}

async function handleVerify() {
  error.value = ''
  if (!/^\d{6}$/.test(code.value)) { error.value = 'کد باید 6 رقمی باشد'; return }
  loadingVerify.value = true
  try {
    const res = await verifyOtp(identifier.value.trim(), code.value.trim())
    // on success verifyOtp performs storage & navigation
    if (res && res.token) {
      localStorage.removeItem('otpExpiry')
      localStorage.removeItem('otpIdentifier')
      localStorage.removeItem('otpResendAt')
      clearTimer()
    }
  } catch (err) {
    error.value = (err && err.message) || String(err) || 'خطا در تایید کد'
  } finally { loadingVerify.value = false }
}

async function verifyOtp(identifierParam, codeParam) {
  // call backend VerifyOtp endpoint
  try {
    const resp = await axios.post(`${API_URL}/Authenticate/VerifyOtp`, {
      username: identifierParam,
      otpCode: codeParam
    }, {
      headers: { 'Content-Type': 'application/json' }
    })

    const d = resp && resp.data ? resp.data : {}
    const loginData = extractLoginData(d)
    const token = loginData.token
    const systemMessage = extractGlobalInformationMessage(d)

    if (token) {
      persistLoginData(loginData)
      const isActive = loginData.companyisactive

      // fetch app settings and then redirect accordingly
      // try { await fetchSetting() } catch (e) { console.warn('fetchSetting failed', e) }

      await showSystemInfoModal(systemMessage)
      await redirectAfterLogin(isActive)

      // success
      if (toast && typeof toast.success === 'function') {
        toast.success('ورود با موفقیت انجام شد', { position: 'top-right', timeout: 1500 })
      }

      return { token }
    }

    throw new Error(d.message || d.Message || 'خطا در تایید کد')
  } catch (err) {
    // normalize axios errors
    if (err && err.response && (err.response.status === 401 || err.response.status === 400 || err.response.status === 500)) {
      const msg = (err.response.data && (err.response.data.message || JSON.stringify(err.response.data))) || 'خطا در اعتبارسنجی'
      throw new Error(msg)
    }
    throw err
  }
}

function resendOtp() {
  if (resendDisabled.value) return

  // اگر کد منقضی شده است، نیاز است کاربر مجدداً شناسه/رمز را وارد کند
  if (expired.value) {
    error.value = 'کد منقضی شده است. لطفاً شماره/ایمیل و رمز عبور را مجدداً وارد کنید.'
    backToStep1()
    nextTick(() => { if (passwordInput.value) passwordInput.value.focus() })
    return
  }

  code.value = ''
  handleSend()
}

function backToStep1() {
  step.value = 1
  forgotPasswordMode.value = false
  forgotPasswordStep.value = 1
  forgotPasswordCode.value = ''
  forgotPasswordNewPassword.value = ''
  forgotPasswordConfirmNewPassword.value = ''
  forgotPasswordSubmitting.value = false
  clearForgotPasswordTimer()
  code.value = ''
  error.value = ''
  localStorage.removeItem('otpExpiry')
  localStorage.removeItem('otpIdentifier')
  localStorage.removeItem('otpResendAt')
  clearTimer()
  cancelWebOTP()
  nextTick(() => { if (identifierInput.value) identifierInput.value.focus() })
}

// WebOTP API برای خواندن خودکار کد از SMS
let abortController = null
const API_URL = process.env.VUE_APP_BACKEND_URL;

async function requestWebOTP() {
  // بررسی پشتیبانی از WebOTP API
  if (!('OTPCredential' in window)) {
    console.log('WebOTP API پشتیبانی نمی‌شود')
    return
  }

  try {
    cancelWebOTP()
    abortController = new AbortController()

    navigator.credentials.get({
      otp: { transport: ['sms'] },
      signal: abortController.signal
    })
      .then(otp => {
        if (otp && otp.code) {
          code.value = otp.code
          // خودکار تایید کد
          if (/^\d{6}$/.test(otp.code)) {
            nextTick(() => handleVerify())
          }
        }
      })
      .catch(err => {
        if (err.name !== 'AbortError') {
          console.warn('خطا در خواندن OTP:', err)
        }
      })
  } catch (err) {
    console.warn('خطا در فعال‌سازی WebOTP:', err)
  }
}

function cancelWebOTP() {
  if (abortController) {
    try {
      abortController.abort()
    } catch (e) {
      console.warn('خطا در لغو WebOTP:', e)
    }
    abortController = null
  }
}

function handleOtpInput() {
  // خودکار تایید کد وقتی 6 رقم وارد شد
  if (/^\d{6}$/.test(code.value)) {
    nextTick(() => handleVerify())
  }
}

function onGoogle() {
  if (googleLoading.value) return
  error.value = ''
  googleLoading.value = true

  // open OAuth popup to backend which should handle Google OAuth flow and then
  // postMessage back to window.opener with { type: 'oauth', provider: 'google', token }
  try {
    googleWindow = window.open('/auth/google', 'google_auth', 'width=600,height=700')
  } catch (e) {
    googleWindow = null
  }

  if (!googleWindow) {
    error.value = 'مرورگر مانع باز شدن پنجره ورود شد. لطفاً اجازهٔ باز شدن پنجره‌ها را بدهید.'
    googleLoading.value = false
    return
  }

  // message handler to receive token from popup
  _onGoogleMessage = async function (ev) {
    try {
      if (!ev || ev.origin !== window.location.origin) return
      const d = ev.data || {}
      if (d.type !== 'oauth' || d.provider !== 'google') return
      // close popup and finalize
      try { if (googleWindow && !googleWindow.closed) googleWindow.close() } catch (e) { /* ignore */ }
      if (d.error) {
        error.value = d.error || 'خطا در ورود با گوگل'
        googleLoading.value = false
        return
      }
      const loginData = extractLoginData(d)
      if (loginData.token) {
        googleLoading.value = false
        await completeLogin(d)
      }
    } catch (e) { console.warn(e); googleLoading.value = false }
  }

  window.addEventListener('message', _onGoogleMessage)

  // poll popup to detect closure without a message
  googleInterval = setInterval(() => {
    try {
      // detect popup closed
      if (!googleWindow || googleWindow.closed) {
        clearInterval(googleInterval); googleInterval = null
        window.removeEventListener('message', _onGoogleMessage); _onGoogleMessage = null
        if (googleLoading.value) {
          googleLoading.value = false
          error.value = 'ورود با گوگل انجام نشد.'
        }
        return
      }

      // also check if backend set token in localStorage (fallback when popup doesn't postMessage)
      try {
        const t = localStorage.getItem('token')
        if (t) {
          clearInterval(googleInterval); googleInterval = null
          window.removeEventListener('message', _onGoogleMessage); _onGoogleMessage = null
          googleLoading.value = false
          redirectAfterLogin(localStorage.getItem('companyisactive') === 'true')
        }
      } catch (e) { /* ignore */ }
    } catch (e) { console.warn(e) }
  }, 500)
}

// Global error handlers to surface better messages and capture rejections
function _onWindowError(ev) {
  try { console.error('window.error', ev); error.value = (ev && (ev.message || String(ev.error || ev))) || 'خطای غیرمنتظره' } catch (e) { console.error(e) }
}
function _onUnhandledRejection(ev) {
  try {
    console.error('unhandledrejection', ev && ev.reason)
    const r = ev && ev.reason
    error.value = r && (r.message || (typeof r === 'string' ? r : JSON.stringify(r))) || String(r)
    if (ev && typeof ev.preventDefault === 'function') ev.preventDefault()
  } catch (e) { console.error(e) }
}

onMounted(() => {
  if (route.query.sessionExpired === '1') {
    sessionExpiredModalOpen.value = true
    router.replace({ path: '/login', query: {} })
  }

  // resume timer if present
  try {
    const storedExpiry = Number(localStorage.getItem('otpExpiry') || 0)
    const storedIdentifier = localStorage.getItem('otpIdentifier') || ''
    const storedResend = Number(localStorage.getItem('otpResendAt') || 0)
    if (storedExpiry && storedExpiry > Date.now() && storedIdentifier) {
      identifier.value = storedIdentifier
      expiryTimestamp.value = storedExpiry
      resendAvailableAt.value = storedResend || (Date.now() + 60000)
      step.value = 2
      startTimer()
      nextTick(() => {
        if (codeInput.value) codeInput.value.focus()
        requestWebOTP()
      })
    }
  } catch (e) { console.warn(e) }

  initTurnstile()
  // listen for runtime errors and prevent the dev-server overlay when possible
  window.addEventListener('error', _onWindowError)
  // assign window.onerror to capture more contextual params and suppress default overlay
  try {
    window.__prevOnError = window.onerror
    window.onerror = function (message, source, lineno, colno, err) {
      try {
        console.error('window.onerror', { message, source, lineno, colno, err })
        // prefer human-friendly message when details are absent (cross-origin script errors show 'Script error.')
        if (message === 'Script error.' && !err) {
          error.value = 'خطای اسکریپت خارجی رخ داده است. جزئیات را در کنسول بررسی کنید.'
        } else {
          error.value = (err && err.message) || message || String(err) || 'خطای غیرمنتظره'
        }
      } catch (e) { console.error(e) }
      try { if (typeof event !== 'undefined' && event && typeof event.preventDefault === 'function') event.preventDefault() } catch (e) { console.warn(e) }
      // return true to indicate we've handled the error and prevent the default handler (dev overlay)
      return true
    }
  } catch (e) { console.warn(e) }
  window.addEventListener('unhandledrejection', _onUnhandledRejection)
})

onBeforeUnmount(() => {
  clearTimer()
  clearRegisterTimer()
  clearForgotPasswordTimer()
  cancelWebOTP()
  try { if (window.turnstile && turnstileWidgetId.value !== null) window.turnstile.reset(turnstileWidgetId.value) } catch (e) { console.warn(e) }
  try { window.removeEventListener('error', _onWindowError) } catch (e) { /* ignore */ }
  try { window.removeEventListener('unhandledrejection', _onUnhandledRejection) } catch (e) { /* ignore */ }
  try { if (window.__prevOnError !== undefined) { window.onerror = window.__prevOnError; delete window.__prevOnError } } catch (e) { /* ignore */ }
  try {
    if (_onGoogleMessage) { window.removeEventListener('message', _onGoogleMessage); _onGoogleMessage = null }
  } catch (e) { /* ignore */ }
  try { if (googleInterval) { clearInterval(googleInterval); googleInterval = null } } catch (e) { /* ignore */ }
  try { if (googleWindow && !googleWindow.closed) { googleWindow.close(); googleWindow = null } } catch (e) { /* ignore */ }
})
</script>

<style scoped>
.login-page3 {
  --login-page-bg: var(--background-color);
  --login-surface: var(--background-color-secondary);
  --login-surface-raised: var(--background-color);
  --login-text: var(--text-color-primary);
  --login-muted: var(--text-color-secondary);
  --login-border: var(--form-control-border, rgba(var(--text-color-secondary-rgb), 0.28));
  --login-accent: var(--background-color-button);
  --login-accent-hover: var(--background-color-button-hover);
  --login-danger: var(--background-color-danger);
  display: flex;
  min-height: 100vh;
  direction: rtl;
  font-family: "IRANSans", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial;
  background: var(--login-page-bg);
  color: var(--login-text);
  position: relative;
  /* allow absolute-positioned back button */
  align-items: stretch;
}

.hero {
  flex: 1 1 60%;
  /* gradient background using requested colors */
  background: linear-gradient(180deg, var(--login-surface) 0%, var(--login-page-bg) 100%);
  color: var(--login-text);
  display: flex;
  flex-direction: column;
  /* center hero content horizontally and vertically */
  direction: rtl;
  /* keep text flow Persian-friendly */
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.hero-inner {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  /* nudge the hero content 40px to the left from true center */
  transform: translateX(-40px);
}

.logo-wrap {
  position: relative;
  width: 100%;
  max-width: clamp(260px, 45vw, 680px);
  /* flexible width that grows/shrinks */
  height: auto;
  display: flex;
  flex-direction: column;
  /* stack image and + vertically so + won't overlay the image */
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.plane {
  width: 100%;
  height: auto;
  max-width: 100%;
  display: block;
  object-fit: contain;
  filter: drop-shadow(0 6px 12px rgba(0, 0, 0, 0.08));
}

.hero-text {
  text-align: center;
  margin-top: clamp(12px, 3vw, 40px);
  width: 100%;
  max-width: clamp(260px, 45vw, 600px);
}

.hero-text h1 {
  font-size: clamp(1.15rem, 2.2vw, 2.2rem);
  margin: 0px 0px 20px 0px;
  font-weight: 700;
  line-height: 1.7;
}

.hero-text h2 {
  font-size: clamp(0.95rem, 2.2vw, 1.25rem);
  margin: 6px 0 18px;
  opacity: 0.95;
}


.cta:hover,
.cta:focus {
  transform: translateY(-3px) scale(1.01);
  box-shadow: 0 14px 40px rgba(var(--background-color-button-rgb), 0.18), 0 6px 18px rgba(var(--text-color-secondary-rgb), 0.08);
  background-color: var(--login-accent-hover);
  /* hover color */
  opacity: 1;
}

.cta:active {
  transform: translateY(-1px) scale(0.995);
  box-shadow: 0 6px 18px rgba(var(--background-color-button-rgb), 0.16);
  background-color: var(--login-accent-hover);
  /* active/pressed color */
}

.cta:focus-visible {
  outline: 3px solid var(--focus-outline);
  outline-offset: 4px;
}

.auth-card {
  width: clamp(300px, 32vw, 420px);
  max-width: 420px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 32px;
  /* place auth card above the hero background on wide screens */
  position: absolute;
  right: clamp(12px, 4vw, 64px);
  top: 50%;
  transform: translateY(-50%);
  z-index: 20;
}

.card-inner {
  width: 100%;
  background: var(--login-surface-raised);
  border: 1px solid var(--login-border);
  border-radius: 14px;
  padding: 28px 28px 20px;
  box-shadow: 0 8px 40px rgba(var(--text-color-secondary-rgb), 0.12);
  position: relative;
}

.lang {
  position: absolute;
  top: 12px;
  left: 12px;
  background: transparent;
  font-weight: 600;
  color: var(--login-muted);
}

.card-title {
  color: var(--login-accent);
  /* use bright blue for heading */
  text-align: center;
  margin-top: 6px;
  margin-bottom: 10px;
  /* larger, responsive heading for login card */
  font-size: clamp(1.05rem, 2.2vw, 1.5rem);
  font-weight: 800;
}

.card-title-divider {
  position: relative;
  width: min(150px, 46%);
  height: 14px;
  margin: 0 auto 18px;
}

.card-title-divider::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: var(--login-border);
  transform: translateY(-50%);
}

.card-title-divider::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--login-accent);
  box-shadow: 0 0 0 5px var(--login-surface-raised);
  transform: translate(-50%, -50%);
}

.card-tabs {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-bottom: 12px
}

.form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.tab-enter-active,
.tab-leave-active {
  transition: opacity 240ms cubic-bezier(.2, .9, .2, 1), transform 240ms cubic-bezier(.2, .9, .2, 1);
}

.tab-enter-from {
  opacity: 0;
  transform: translateX(10px);
}

.tab-enter-to {
  opacity: 1;
  transform: translateX(0);
}

.tab-leave-from {
  opacity: 1;
  transform: translateX(0);
}

.tab-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}

@media (prefers-reduced-motion: reduce) {

  .tab-enter-active,
  .tab-leave-active {
    transition: none;
  }
}

.input {
  border-radius: 6px;
  border: 1px solid var(--login-border);
  background: var(--form-control-bg);
  color: var(--login-text);
  outline: none;
}

/* Native OTP/register inputs and the controls inside FloatingInput must share
   the same themed surface, text, border, placeholder and focus treatment. */
.login-page3 > .auth-card input.input,
.login-page3 :deep(.input .kb-field__control) {
  border-color: var(--login-border);
  background: var(--form-control-bg);
  color: var(--login-text);
}

.login-page3 > .auth-card input.input::placeholder,
.login-page3 :deep(.input .kb-field__control::placeholder) {
  color: var(--login-muted);
}

.login-page3 > .auth-card input.input:focus,
.login-page3 :deep(.input .kb-field__control:focus) {
  border-color: var(--login-accent);
  box-shadow: 0 0 0 3px var(--focus-outline);
}

.password-input {
  margin-top: 16px;
}

.links {
  font-size: 12px;
  color: var(--login-muted);
  margin-top: 10px;
  line-height: 1.6;
  text-align: center;
}

.forgot-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.forgot-code-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 10px;
}

.forgot-code-help {
  flex: 1;
  color: var(--login-muted);
  font-size: 12px;
  line-height: 1.7;
  margin: 0;
}

.forgot-code-timer {
  color: var(--login-muted);
  direction: ltr;
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 700;
}

.forgot-code-submit,
.forgot-reset-submit {
  margin-top: 18px;
  font-size: 22px;
}

.forgot-code-back,
.forgot-reset-back {
  margin-top: 12px;
}

.divider {
  text-align: center;
  margin: 30px 0;
  color: var(--login-muted);
}

.center-btn {
  display: flex;
  justify-content: center;
}

.g-icon {
  vertical-align: middle;
  width: 20px;
  height: 20px;
  display: block;
  object-fit: contain;
}

.terms {
  font-size: 12px;
  color: var(--login-muted);
  text-align: center;
  margin-top: 12px;
}

.terms a {
  color: var(--login-accent);
  text-decoration: underline
}

.field-label {
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
  color: var(--login-text);
}

.otp-step .input {
  width: 100%;
}

.register-step .input {
  width: 100%;
}

.otp-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px
}

.timer {
  color: var(--login-muted);
  font-size: 13px;
  font-weight: 600
}

.timer-warning {
  color: var(--background-color-warning);
  animation: pulse 1.5s ease-in-out infinite
}

@keyframes pulse {

  0%,
  100% {
    opacity: 1
  }

  50% {
    opacity: 0.7
  }
}

.otp-actions {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-top: 12px
}

.otp-secondary {
  margin-top: 12px;
  display: flex;
  justify-content: center
}

.error {
  color: var(--login-danger);
  margin-top: 10px;
  text-align: center
}

.back-site {
  position: absolute;
  top: 18px;
  right: 18px;
  background: var(--login-surface-raised);
  color: var(--login-muted);
  border: 1px solid var(--login-border);
  padding: 8px 14px;
  border-radius: 20px;
  font-weight: 700;
  box-shadow: 0 6px 18px rgba(var(--text-color-secondary-rgb), 0.1);
  z-index: 30;
  /* make sure it appears above hero/auth-card */
  text-decoration: none;
}

.back-site:hover {
  color: var(--login-text);
  opacity: 0.95
}

.system-info-message {
  line-height: 1.9;
  white-space: pre-line;
}

/* Responsive: on narrow screens stack card below hero and make hero flexible */
@media (max-width:900px) {
  .login-page3 {
    flex-direction: column
  }

  /* keep auth card a comfortable size on tablet rather than full-width */
  .auth-card {
    width: min(420px, 92%);
    padding: 22px;
    position: static !important;
    transform: none;
    max-width: 420px;
    margin: 50px auto 40px
  }

  /* hide hero on tablet and smaller so auth-card is the focus */
  .hero {
    display: none
  }

  .logo-wrap {
    max-width: 60%;
  }

  .hero-text {
    max-width: 95%;
    margin-top: 20px
  }

  .hero-text h1 {
    font-size: clamp(1rem, 5vw, 1.5rem)
  }

  /* reduce the left nudge on narrower viewports so layout feels balanced */
  .hero-inner {
    transform: translateX(-20px)
  }
}

/* On mobile (phones) hide the hero to focus on the auth card */
@media (max-width:600px) {
  .hero {
    display: none
  }

  /* ensure layout stacks cleanly when hero is hidden */
  .login-page3 {
    flex-direction: column
  }

  /* limit width on phones too so the card has comfortable side margins */
  .auth-card {
    width: min(420px, 92%);
    padding: 18px;
    position: static !important;
    transform: none;
    max-width: 420px;
    margin: 50px auto 28px
  }
}
</style>
